import { request } from '@umijs/max';

export type Envelope<T> = {
  code: number;
  message: string;
  data: T;
  request_id: string;
};

export type AdminUploadImageData = {
  file_name: string;
  file_url: string;
  mime_type: string;
  size: number;
};

export type PaginatedList<T> = {
  list: T[];
  pagination?: {
    total?: number;
    page?: number;
    page_size?: number;
  };
};

export type CurrentUser = {
  user_id: string;
  primary_role: string;
  account_status: string;
  nickname?: string;
  avatar_url?: string | null;
  auth_context?: 'miniapp' | 'admin';
  admin_role_id?: string | null;
  admin_role_code?: string | null;
  admin_role_name?: string | null;
  permissions?: string[];
  session_version?: number;
};

export type AdminDashboardData = {
  summary_cards?: {
    total_members?: number;
    total_brokers?: number;
    total_leaders?: number;
    register_income_pending?: number;
    custom_income_pending?: number;
  };
  pending_counts?: {
    withdrawals?: number;
    meeting_followups?: number;
    post_reviews?: number;
    broker_audits?: number;
    leader_audits?: number;
    store_applications?: number;
  };
  trend_data?: {
    dates?: string[];
    register_income?: number[];
    custom_income?: number[];
  };
};

export type AdminMemberSourceType =
  | 'leader_direct'
  | 'broker_invite'
  | 'member_invite'
  | 'store_natural'
  | 'organic_share';

export type AdminLeaderMemberSourceType =
  | 'leader_direct'
  | 'broker_invite'
  | 'store_natural';

export type AdminBrokerMemberSourceType = 'broker_direct' | 'assigned';

export type AdminLeaderSourceStats = {
  leader_direct: number;
  broker_invite: number;
  store_natural: number;
};

export type AdminBrokerSourceStats = {
  broker_direct: number;
  assigned: number;
};

export type AdminMemberListItem = {
  member_id: string;
  user_id?: string;
  has_member_profile?: boolean;
  display_name: string;
  gender?: string | null;
  age?: number | null;
  city?: string | null;
  member_status: string;
  realname_status?: 'pending' | 'passed' | null;
  realname_verified?: boolean;
  auth_methods?: string[];
  register_source?: string | null;
  payment_status?: string | null;
  broker_name?: string | null;
  broker_binding_status?: 'bound' | 'unbound';
  leader_name?: string | null;
  source_type?: AdminMemberSourceType | null;
  account_status?: string | null;
  phone?: string | null;
  wechat_id?: string | null;
  last_active_at?: string | null;
  updated_at?: string | null;
};

export type AdminMemberDetailData = {
  has_member_profile?: boolean;
  profile?: {
    member_id: string;
    user_id: string;
    display_name: string;
    real_name?: string | null;
    phone?: string | null;
    wechat_id?: string | null;
    id_card_no?: string | null;
    id_card_address?: string | null;
    id_card_front_url?: string | null;
    gender?: string | null;
    birth_year?: number | null;
    age?: number | null;
    height_cm?: number | null;
    weight_kg?: number | null;
    city?: string | null;
    hometown_city?: string | null;
    bio?: string | null;
    profession?: string | null;
    education_level?: string | null;
    income_range?: string | null;
    marital_status?: string | null;
    personality?: string | null;
    family_income_range?: string | null;
    family_member_summary?: string | null;
    debt_status?: string | null;
    criminal_record_status?: string | null;
    housing_status?: string | null;
    car_status?: string | null;
    member_status: string;
    realname_status?: 'pending' | 'passed' | null;
    realname_verified?: boolean;
    auth_methods?: string[];
    register_source?: string | null;
    is_profile_public?: boolean;
    account_status?: string | null;
    last_active_at?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
  };
  verification_records?: {
    verification_type: string;
    verification_status: string;
    provider?: string | null;
    review_note?: string | null;
    verified_at?: string | null;
  }[];
  orders?: {
    order_no: string;
    business_type: string;
    amount: number;
    payment_status: string;
    paid_at?: string | null;
    created_at?: string | null;
  }[];
  meetings?: {
    meeting_request_id: string;
    meeting_status: string;
    preferred_time_text?: string | null;
    updated_at?: string | null;
  }[];
  ownership?: {
    broker_user_id: string;
    broker_name?: string | null;
    leader_user_id?: string | null;
    leader_name?: string | null;
    ownership_status?: string | null;
    bound_at?: string | null;
    adjust_reason?: string | null;
  } | null;
  violations?: {
    type: string;
    status?: string | null;
    content?: string | null;
    created_at?: string | null;
  }[];
  photos?: {
    photo_id: string;
    photo_url: string;
    photo_type?: string | null;
    sort_order?: number | null;
    audit_status?: string | null;
    created_at?: string | null;
  }[];
  preference?: {
    preferred_age_min?: number | null;
    preferred_age_max?: number | null;
    preferred_height_min?: number | null;
    preferred_height_max?: number | null;
    preferred_weight_text?: string | null;
    preferred_income_text?: string | null;
    preferred_city?: string | null;
    preferred_education?: string | null;
    preferred_marital_status?: string | null;
    preferred_house_car_requirement?: string | null;
    preferred_notes?: string | null;
  } | null;
  posts?: {
    post_id: string;
    content: string;
    audit_status?: string | null;
    created_at?: string | null;
  }[];
  chat_risk_records?: {
    message_id: string;
    content: string;
    security_status?: string | null;
    contact_block_status?: string | null;
    sent_at?: string | null;
  }[];
  notes?: {
    note_id: string;
    content: string;
    operator_name?: string | null;
    created_at?: string | null;
  }[];
};

export type AdminEarningSummary = {
  total_earnings?: number;
  pending_withdrawal_amount?: number;
  available_amount?: number;
};

export type AdminBrokerListItem = {
  broker_id: string;
  user_id?: string;
  broker_name: string;
  mobile?: string | null;
  leader_user_id?: string | null;
  service_city?: string | null;
  service_region?: string | null;
  wechat_id?: string | null;
  bank_account_name?: string | null;
  bank_name?: string | null;
  bank_card_no_masked?: string | null;
  bank_card_no?: string | null;
  application_source?: string | null;
  broker_status: string;
  audit_status?: string | null;
  audit_note?: string | null;
  audited_at?: string | null;
  audited_by_admin_id?: string | null;
  member_count?: number;
  total_earnings?: number;
  available_amount?: number;
  pending_withdrawal_amount?: number;
};

export type AdminBrokerDetailData = {
  profile?: {
    broker_id: string;
    user_id: string;
    broker_name: string;
    mobile?: string | null;
    leader_user_id?: string | null;
    service_city?: string | null;
    service_region?: string | null;
    profile_intro?: string | null;
    bank_account_name?: string | null;
    bank_name?: string | null;
    bank_card_no_masked?: string | null;
    bank_card_no?: string | null;
    application_source?: string | null;
    broker_status: string;
    audit_status?: string | null;
    audit_note?: string | null;
    audited_at?: string | null;
    audited_by_admin_id?: string | null;
    invite_code?: string | null;
  };
  personal_profile?: {
    user_id: string;
    nickname?: string | null;
    mobile?: string | null;
    avatar_url?: string | null;
    account_status?: string | null;
    register_source?: string | null;
    display_name?: string | null;
    real_name?: string | null;
    gender?: string | null;
    age?: number | null;
    current_city?: string | null;
    profession?: string | null;
    education_level?: string | null;
    marital_status?: string | null;
    bio?: string | null;
    member_status?: string | null;
    id_card_no?: string | null;
    id_card_address?: string | null;
    id_card_front_url?: string | null;
  };
  relationship?: {
    leader?: {
      leader_user_id: string;
      leader_name: string;
      service_city?: string | null;
      leader_status: string;
      leader_mobile?: string | null;
      leader_bound_at?: string | null;
      leader_bind_source?: string | null;
    } | null;
  };
  photos?: {
    photo_id: string;
    photo_url?: string | null;
    photo_type?: string | null;
    sort_order?: number | null;
    audit_status?: string | null;
    created_at?: string | null;
  }[];
  members?: {
    member_user_id: string;
    display_name?: string | null;
    city?: string | null;
    member_status?: string | null;
    source_type?: AdminBrokerMemberSourceType | null;
  }[];
  source_stats?: AdminBrokerSourceStats;
  meetings?: {
    meeting_request_id: string;
    meeting_status: string;
    from_member_user_id?: string | null;
    from_member_name?: string | null;
    target_member_user_id?: string | null;
    target_member_name?: string | null;
    owned_member_names?: string[];
    leader_user_id?: string | null;
    leader_name?: string | null;
    preferred_time_text?: string | null;
    confirmed_time?: string | null;
    store_name?: string | null;
    store_address?: string | null;
    read_only?: boolean;
    can_operate?: boolean;
    updated_at?: string | null;
  }[];
  earnings?: {
    ledger_id: string;
    amount: number;
    ledger_status: string;
    business_type?: string | null;
    created_at?: string | null;
  }[];
  withdrawals?: {
    withdrawal_id: string;
    amount: number;
    withdrawal_status: string;
    created_at?: string | null;
  }[];
  earning_summary?: AdminEarningSummary;
};

export type AdminLeaderListItem = {
  leader_id: string;
  user_id?: string;
  leader_name: string;
  mobile?: string | null;
  service_city?: string | null;
  service_region?: string | null;
  wechat_id?: string | null;
  bank_account_name?: string | null;
  bank_name?: string | null;
  bank_card_no_masked?: string | null;
  bank_card_no?: string | null;
  application_source?: string | null;
  leader_status: string;
  audit_status?: string | null;
  audit_note?: string | null;
  audited_at?: string | null;
  audited_by_admin_id?: string | null;
  store_name?: string | null;
  broker_names?: string | null;
  broker_count?: number;
  member_count?: number;
  custom_intent_count?: number;
  total_earnings?: number;
  pending_withdrawal_amount?: number;
  available_amount?: number;
};

export type AdminLeaderDetailData = {
  profile?: {
    leader_id: string;
    user_id: string;
    leader_name: string;
    mobile?: string | null;
    service_city?: string | null;
    service_region?: string | null;
    resource_intro?: string | null;
    bank_account_name?: string | null;
    bank_name?: string | null;
    bank_card_no_masked?: string | null;
    bank_card_no?: string | null;
    application_source?: string | null;
    leader_status: string;
    leader_mobile?: string | null;
    audit_status?: string | null;
    audit_note?: string | null;
    audited_at?: string | null;
    audited_by_admin_id?: string | null;
  };
  personal_profile?: {
    user_id: string;
    nickname?: string | null;
    mobile?: string | null;
    avatar_url?: string | null;
    account_status?: string | null;
    register_source?: string | null;
    display_name?: string | null;
    real_name?: string | null;
    gender?: string | null;
    age?: number | null;
    current_city?: string | null;
    profession?: string | null;
    education_level?: string | null;
    marital_status?: string | null;
    bio?: string | null;
    member_status?: string | null;
    id_card_no?: string | null;
    id_card_address?: string | null;
    id_card_front_url?: string | null;
  };
  brokers?: {
    broker_id: string;
    broker_user_id: string;
    broker_name: string;
    service_city?: string | null;
    service_region?: string | null;
    broker_status: string;
    broker_mobile?: string | null;
    member_count?: number;
    leader_bound_at?: string | null;
  }[];
  members?: {
    member_user_id: string;
    display_name?: string | null;
    city?: string | null;
    member_status?: string | null;
    source_type?: AdminLeaderMemberSourceType | null;
  }[];
  source_stats?: AdminLeaderSourceStats;
  meetings?: {
    meeting_request_id: string;
    meeting_status: string;
    preferred_time_text?: string | null;
    updated_at?: string | null;
  }[];
  custom_intents?: {
    intent_id: string;
    member_user_id: string;
    member_name?: string | null;
    intent_status: string;
    service_tier_display?: string | null;
  }[];
  earnings?: {
    ledger_id: string;
    amount: number;
    ledger_status: string;
    business_type?: string | null;
    created_at?: string | null;
  }[];
  withdrawals?: {
    withdrawal_id: string;
    amount: number;
    withdrawal_status: string;
    created_at?: string | null;
  }[];
  earning_summary?: AdminEarningSummary;
  photos?: {
    photo_id: string;
    photo_url?: string | null;
    photo_type?: string | null;
    sort_order?: number | null;
    audit_status?: string | null;
    created_at?: string | null;
  }[];
};

export type AdminStoreManagerListItem = {
  manager_id: string;
  user_id?: string;
  manager_name: string;
  mobile?: string | null;
  wechat_id?: string | null;
  account_status?: string | null;
  manager_status: string;
  source_type?: 'admin_create' | 'miniapp_apply';
  store_count?: number;
  active_store_count?: number;
  store_names?: string | null;
  leader_count?: number;
  total_earnings?: number;
  pending_withdrawal_amount?: number;
  available_amount?: number;
  bank_account_name?: string | null;
  bank_name?: string | null;
  bank_card_no?: string | null;
  bank_card_no_masked?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type AdminStoreManagerDetailData = {
  profile?: AdminStoreManagerListItem;
  personal_profile?: AdminBrokerDetailData['personal_profile'];
  stores?: {
    store_id: string;
    store_name: string;
    address?: string | null;
    manager_name?: string | null;
    primary_phone?: string | null;
    wechat_id?: string | null;
    source_type?: 'admin_create' | 'miniapp_apply';
    bank_account_name?: string | null;
    bank_name?: string | null;
    bank_card_no?: string | null;
    bank_card_no_masked?: string | null;
    image_urls?: string[];
    store_status?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
  }[];
  leaders?: {
    assignment_id: string;
    store_id: string;
    store_name?: string | null;
    leader_user_id: string;
    leader_name?: string | null;
    leader_status?: string | null;
    mobile?: string | null;
    assignment_status?: string | null;
    created_at?: string | null;
  }[];
  earnings?: {
    ledger_id: string;
    amount: number;
    ledger_status?: string | null;
    business_type?: string | null;
    remark?: string | null;
    created_at?: string | null;
  }[];
  withdrawals?: {
    withdrawal_id: string;
    amount: number;
    withdrawal_status?: string | null;
    created_at?: string | null;
  }[];
  photos?: {
    photo_id: string;
    photo_url?: string | null;
    photo_type?: string | null;
    sort_order?: number | null;
    audit_status?: string | null;
    created_at?: string | null;
  }[];
};

export type AdminMeetingListItem = {
  meeting_request_id: string;
  from_member_user_id: string;
  from_member_name?: string | null;
  target_member_user_id: string;
  target_member_name?: string | null;
  leader_user_id?: string | null;
  leader_name?: string | null;
  store_id?: string | null;
  store_name?: string | null;
  store_address?: string | null;
  preferred_time_text?: string | null;
  meeting_status: string;
  leader_note?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
};

export type AdminMeetingDetailData = {
  profile?: AdminMeetingListItem & {
    confirmed_time?: string | null;
    cancel_reason?: string | null;
  };
  timeline?: {
    event_id: string;
    event_type: string;
    event_label: string;
    note?: string | null;
    created_at?: string | null;
  }[];
};

export type AdminCustomIntentListItem = {
  intent_id: string;
  member_user_id: string;
  member_name?: string | null;
  member_mobile?: string | null;
  leader_user_id?: string | null;
  leader_name?: string | null;
  intent_status: string;
  service_tier_display?: string | null;
  payment_status?: string | null;
  user_note?: string | null;
  contact_note?: string | null;
  created_at?: string | null;
};

export type AdminCustomIntentDetailData = {
  profile?: AdminCustomIntentListItem & {
    close_reason?: string | null;
    updated_at?: string | null;
  };
  member_profile?: {
    display_name?: string | null;
    gender?: string | null;
    age?: number | null;
    city?: string | null;
  } | null;
  orders?: {
    order_no: string;
    amount: number;
    payment_status: string;
    paid_at?: string | null;
    created_at?: string | null;
  }[];
  followups?: {
    followup_id: string;
    target_status: string;
    note: string;
    leader_user_id?: string | null;
    created_at?: string | null;
  }[];
  stats?: {
    is_high_value_customer?: boolean;
  };
};

export type AdminOrderListItem = {
  order_no: string;
  user_id: string;
  user_name?: string | null;
  business_type: string;
  amount: number;
  payment_status: string;
  paid_at?: string | null;
  created_at?: string | null;
};

export type AdminEarningListItem = {
  ledger_id: string;
  user_id: string;
  user_name?: string | null;
  role_type: string;
  business_type: string;
  amount: number;
  ledger_status: string;
  remark?: string | null;
  created_at?: string | null;
};

export type AdminIncomeHierarchyBroker = {
  broker_user_id: string;
  broker_name: string;
  broker_mobile?: string | null;
  wechat_id?: string | null;
  income: number;
  member_count: number;
};

export type AdminIncomeHierarchyLeader = {
  leader_user_id: string;
  leader_name: string;
  leader_mobile?: string | null;
  wechat_id?: string | null;
  income: number;
  brokers: AdminIncomeHierarchyBroker[];
};

export type AdminIncomeHierarchyStore = {
  store_id: string;
  store_name: string;
  manager_name?: string | null;
  manager_mobile?: string | null;
  income: number;
  leaders: AdminIncomeHierarchyLeader[];
};

export type AdminIncomeHierarchyData = {
  date_range?: {
    start_at?: string | null;
    end_at?: string | null;
  };
  summary?: {
    total_stores?: number;
    total_leaders?: number;
    total_brokers?: number;
    total_members?: number;
    store_income?: number;
    leader_income?: number;
    broker_income?: number;
    total_income?: number;
  };
  list: AdminIncomeHierarchyStore[];
};

export type AdminWithdrawalListItem = {
  withdrawal_id: string;
  user_id: string;
  user_name?: string | null;
  role_type: string;
  amount: number;
  total_earnings?: number;
  pending_withdrawal_amount?: number;
  available_amount?: number;
  withdrawal_status: string;
  review_note?: string | null;
  reviewed_at?: string | null;
  created_at?: string | null;
  withdraw_account_snapshot?: Record<string, unknown> | null;
};

export type AdminWithdrawalDetailData = {
  profile?: AdminWithdrawalListItem & {
    paid_at?: string | null;
  };
  summary?: AdminEarningSummary;
  earning_records?: {
    ledger_id: string;
    amount: number;
    ledger_status: string;
    business_type?: string | null;
    remark?: string | null;
    created_at?: string | null;
  }[];
  history_withdrawals?: {
    withdrawal_id: string;
    amount: number;
    withdrawal_status: string;
    review_note?: string | null;
    created_at?: string | null;
    reviewed_at?: string | null;
  }[];
};

export type AdminPostListItem = {
  post_id: string;
  user_id: string;
  display_name?: string | null;
  topic_id?: string | null;
  topic_title?: string | null;
  content: string;
  audit_status: string;
  audit_note?: string | null;
  security_check_status?: string | null;
  contact_block_status?: string | null;
  image_count?: number;
  image_urls?: string[];
  like_count?: number;
  comment_count?: number;
  created_at?: string | null;
  updated_at?: string | null;
};

export type AdminMemberAlbumListItem = {
  photo_id: string;
  member_id: string;
  user_id: string;
  display_name?: string | null;
  current_city?: string | null;
  photo_url: string;
  photo_type?: string | null;
  sort_order?: number | null;
  audit_status: string;
  created_at?: string | null;
};

export type AdminStoreListItem = {
  store_id: string;
  application_id?: string | null;
  record_type?: 'store' | 'store_application';
  source_type?: 'admin_create' | 'miniapp_apply';
  audit_status?: 'not_required' | 'pending' | 'approved' | 'rejected';
  store_name: string;
  address?: string | null;
  manager_name?: string | null;
  primary_phone?: string | null;
  wechat_id?: string | null;
  owner_user_id?: string | null;
  owner_display_name?: string | null;
  income_owner_type?: 'store_owner' | 'official';
  income_owner_label?: string | null;
  total_earnings?: number;
  pending_withdrawals?: number;
  available_earnings?: number;
  bank_account_name?: string | null;
  bank_name?: string | null;
  bank_card_no?: string | null;
  image_urls?: string[];
  geo_status?: string | null;
  leader_count?: number;
  meeting_count?: number;
  store_status: string;
  created_at?: string | null;
  updated_at?: string | null;
};

export type AdminStoreApplicationListItem = {
  application_id: string;
  user_id: string;
  store_name: string;
  address?: string | null;
  manager_name?: string | null;
  mobile?: string | null;
  wechat_id?: string | null;
  bank_account_name?: string | null;
  bank_name?: string | null;
  bank_card_no?: string | null;
  image_urls?: string[];
  application_note?: string | null;
  application_source?: string | null;
  application_status: string;
  audit_note?: string | null;
  approved_store_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type AdminStoreDetailData = {
  profile?: AdminStoreListItem & {
    image_urls?: string[];
    wechat_id?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    geo_updated_at?: string | null;
  };
  leaders?: {
    leader_user_id: string;
    leader_name?: string | null;
    assignment_status?: string | null;
    created_at?: string | null;
  }[];
  meetings?: {
    meeting_request_id: string;
    meeting_status: string;
    from_member_name?: string | null;
    target_member_name?: string | null;
    preferred_time_text?: string | null;
    created_at?: string | null;
  }[];
  stats?: {
    leader_count?: number;
    meeting_count?: number;
  };
  income_summary?: {
    total_earnings?: number;
    pending_withdrawals?: number;
    available_earnings?: number;
  };
  earning_records?: {
    ledger_id: string;
    amount?: number;
    ledger_status?: string | null;
    business_type?: string | null;
    remark?: string | null;
    created_at?: string | null;
  }[];
};

export type DistrictTreeNode = {
  id: string;
  name: string;
  fullname: string;
  cities: {
    id: string;
    name: string;
    fullname: string;
    districts: {
      id: string;
      name: string;
      fullname: string;
    }[];
  }[];
};

export type DistrictListData = {
  data_version: string;
  list: DistrictTreeNode[];
};

export type AdminArticleListItem = {
  article_id: string;
  title: string;
  content_html?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type AdminPartnerServiceListItem = {
  partner_service_id: string;
  merchant_name: string;
  address?: string | null;
  description?: string | null;
  contact_phone?: string | null;
  wechat_contact_hint?: string | null;
  cover_url?: string | null;
  image_urls?: string[];
  sort_order?: number;
  service_status: string;
};

export type AdminCompanyStoreListItem = {
  company_store_id: string;
  store_name: string;
  address?: string | null;
  description?: string | null;
  contact_phone?: string | null;
  cover_url?: string | null;
  image_urls?: string[];
  sort_order?: number;
  service_status: string;
};

export type AdminTopicListItem = {
  topic_id: string;
  topic_key?: string;
  title: string;
  intro?: string | null;
  icon_url?: string | null;
  sort_order?: number;
  status: 'active' | 'inactive';
  post_count?: number;
};

export type AdminSettingsData = {
  pricing?: Record<string, number>;
  switches?: Record<string, boolean>;
  risk_rules?: Record<string, unknown>;
  copywriting?: Record<string, unknown> & {
    support_contact?: {
      service_chat_corp_id?: string;
      service_chat_url?: string;
      show_message_card?: boolean;
    };
  };
  home_operations?: Record<string, unknown>;
  front_content?: Record<string, unknown>;
  profile_explanation_pages?: Record<string, unknown>;
  promotion_settings?: Record<string, unknown>;
  custom_service_page?: Record<string, unknown>;
  communication_fee_page?: Record<string, unknown>;
  withdrawal_rules?: {
    min_amount?: number;
    max_amount?: number;
    daily_max_count?: number;
    request_window_start?: string;
    request_window_end?: string;
    arrival_text?: string;
    amount_unit?: string;
    extra_notes?: string[];
  };
};

export type AdminAccountItem = {
  admin_user_id: string;
  login_account?: string | null;
  nickname?: string | null;
  account_status: string;
  last_login_at?: string | null;
  updated_at?: string | null;
  action_count?: number;
  admin_role_id?: string | null;
  admin_role_code?: string | null;
  admin_role_name?: string | null;
  is_super_admin?: boolean;
  permissions?: string[];
  notes?: string | null;
  session_version?: number;
  last_forced_logout_at?: string | null;
};

export type PermissionCatalogItem = {
  key: string;
  label: string;
};

export type PermissionCatalogGroup = {
  key: string;
  name: string;
  permissions: PermissionCatalogItem[];
};

export type AdminRoleItem = {
  role_id: string;
  code: string;
  name: string;
  description?: string | null;
  is_system: boolean;
  permissions: string[];
  account_count: number;
  created_at?: string | null;
  updated_at?: string | null;
};

export type AdminLogItem = {
  log_id: string;
  admin_user_id?: string;
  operator_name?: string | null;
  action_type: string;
  target_type: string;
  target_id?: string | null;
  created_at?: string | null;
  action_snapshot?: Record<string, unknown> | null;
};

export type AdminReportsData = {
  overview?: {
    total_members?: number;
    active_members?: number;
    register_income?: number;
    custom_income?: number;
    pending_withdrawals?: number;
    completed_meetings?: number;
    resolved_reports?: number;
    active_stores?: number;
  };
  conversion_funnel?: {
    label: string;
    value: number;
  }[];
  risk_breakdown?: {
    report_type: string;
    count: number;
  }[];
  income_breakdown?: {
    business_type: string;
    amount: number;
  }[];
};

export async function adminPasswordLogin(account: string, password: string) {
  return request<Envelope<{ token: string; user: CurrentUser }>>('/auth/admin-password-login', {
    method: 'POST',
    skipErrorHandler: true,
    data: {
      account,
      password,
    },
  });
}

export async function getCurrentUser() {
  return request<Envelope<CurrentUser>>('/users/me', { method: 'GET' });
}

export async function getAdminDashboard() {
  return request<Envelope<AdminDashboardData>>('/admin/dashboard', { method: 'GET' });
}

export async function getAdminMembers(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminMemberListItem>>>('/admin/members', {
    method: 'GET',
    params,
  });
}

export async function getAdminMemberDetail(memberId: string) {
  return request<Envelope<AdminMemberDetailData>>(`/admin/members/${memberId}`, {
    method: 'GET',
  });
}

export async function createAdminMember(data: Record<string, unknown>) {
  return request<Envelope<Record<string, unknown>>>('/admin/members', {
    method: 'POST',
    data,
  });
}

export async function updateAdminMember(memberId: string, data: Record<string, unknown>) {
  return request<Envelope<{ success: boolean }>>(`/admin/members/${memberId}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteAdminMember(memberId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/members/${memberId}`, {
    method: 'DELETE',
    data: { reason },
  });
}

export async function purgeAdminMember(memberId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/members/${memberId}/purge`, {
    method: 'DELETE',
    data: { reason },
  });
}

export async function freezeAdminMember(memberId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/members/${memberId}/freeze`, {
    method: 'POST',
    data: { reason },
  });
}

export async function unfreezeAdminMember(memberId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/members/${memberId}/unfreeze`, {
    method: 'POST',
    data: { reason },
  });
}

export async function adjustAdminMemberOwnership(
  memberId: string,
  data: {
    broker_user_id: string;
    leader_user_id?: string;
    reason: string;
  },
) {
  return request<Envelope<{ success: boolean }>>(`/admin/members/${memberId}/ownership`, {
    method: 'POST',
    data,
  });
}

export async function updateAdminMemberProfileVisibility(
  memberId: string,
  data: {
    is_profile_public: boolean;
    reason: string;
  },
) {
  return request<Envelope<{ success: boolean }>>(
    `/admin/members/${memberId}/profile-visibility`,
    {
      method: 'POST',
      data,
    },
  );
}

export async function createAdminMemberNote(memberId: string, note: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/members/${memberId}/notes`, {
    method: 'POST',
    data: { note },
  });
}

export async function getAdminBrokers(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminBrokerListItem>>>('/admin/brokers', {
    method: 'GET',
    params,
  });
}

export async function getAdminBrokerDetail(brokerId: string) {
  return request<Envelope<AdminBrokerDetailData>>(`/admin/brokers/${brokerId}`, {
    method: 'GET',
  });
}

export async function auditAdminBroker(brokerId: string, targetStatus: string, note?: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/brokers/${brokerId}/audit`, {
    method: 'POST',
    data: {
      target_status: targetStatus,
      note,
    },
  });
}

export async function createAdminBroker(data: Record<string, unknown>) {
  return request<Envelope<Record<string, unknown>>>('/admin/brokers', {
    method: 'POST',
    data,
  });
}

export async function updateAdminBroker(brokerId: string, data: Record<string, unknown>) {
  return request<Envelope<{ success: boolean }>>(`/admin/brokers/${brokerId}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteAdminBroker(brokerId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/brokers/${brokerId}`, {
    method: 'DELETE',
    data: { reason },
  });
}

export async function purgeAdminBroker(brokerId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/brokers/${brokerId}/purge`, {
    method: 'DELETE',
    data: { reason },
  });
}

export async function freezeAdminBroker(brokerId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/brokers/${brokerId}/freeze`, {
    method: 'POST',
    data: { reason },
  });
}

export async function unfreezeAdminBroker(brokerId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/brokers/${brokerId}/unfreeze`, {
    method: 'POST',
    data: { reason },
  });
}

export async function resetAdminBrokerInviteCode(brokerId: string) {
  return request<Envelope<{ success: boolean; invite_code: string }>>(
    `/admin/brokers/${brokerId}/reset-invite-code`,
    {
      method: 'POST',
    },
  );
}

export async function getAdminLeaders(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminLeaderListItem>>>('/admin/leaders', {
    method: 'GET',
    params,
  });
}

export async function getAdminLeaderDetail(leaderId: string) {
  return request<Envelope<AdminLeaderDetailData>>(`/admin/leaders/${leaderId}`, {
    method: 'GET',
  });
}

export async function auditAdminLeader(
  leaderId: string,
  targetStatus: string,
  note?: string,
  storeId?: string,
) {
  return request<Envelope<{ success: boolean }>>(`/admin/leaders/${leaderId}/audit`, {
    method: 'POST',
    data: {
      target_status: targetStatus,
      note,
      store_id: storeId,
    },
  });
}

export async function createAdminLeader(data: Record<string, unknown>) {
  return request<Envelope<Record<string, unknown>>>('/admin/leaders', {
    method: 'POST',
    data,
  });
}

export async function updateAdminLeader(leaderId: string, data: Record<string, unknown>) {
  return request<Envelope<{ success: boolean }>>(`/admin/leaders/${leaderId}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteAdminLeader(leaderId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/leaders/${leaderId}`, {
    method: 'DELETE',
    data: { reason },
  });
}

export async function purgeAdminLeader(leaderId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/leaders/${leaderId}/purge`, {
    method: 'DELETE',
    data: { reason },
  });
}

export async function freezeAdminLeader(leaderId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/leaders/${leaderId}/freeze`, {
    method: 'POST',
    data: { reason },
  });
}

export async function unfreezeAdminLeader(leaderId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/leaders/${leaderId}/unfreeze`, {
    method: 'POST',
    data: { reason },
  });
}

export async function getAdminStoreManagers(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminStoreManagerListItem>>>('/admin/store-managers', {
    method: 'GET',
    params,
  });
}

export async function getAdminStoreManagerDetail(managerId: string) {
  return request<Envelope<AdminStoreManagerDetailData>>(`/admin/store-managers/${managerId}`, {
    method: 'GET',
  });
}

export async function createAdminStoreManager(data: Record<string, unknown>) {
  return request<Envelope<Record<string, unknown>>>('/admin/store-managers', {
    method: 'POST',
    data,
  });
}

export async function updateAdminStoreManager(managerId: string, data: Record<string, unknown>) {
  return request<Envelope<{ success: boolean }>>(`/admin/store-managers/${managerId}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteAdminStoreManager(managerId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/store-managers/${managerId}`, {
    method: 'DELETE',
    data: { reason },
  });
}

export async function purgeAdminStoreManager(managerId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/store-managers/${managerId}/purge`, {
    method: 'DELETE',
    data: { reason },
  });
}

export async function freezeAdminStoreManager(managerId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/store-managers/${managerId}/freeze`, {
    method: 'POST',
    data: { reason },
  });
}

export async function unfreezeAdminStoreManager(managerId: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/store-managers/${managerId}/unfreeze`, {
    method: 'POST',
    data: { reason },
  });
}

export async function getAdminMeetings(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminMeetingListItem>>>('/admin/meetings', {
    method: 'GET',
    params,
  });
}

export async function getAdminMeetingDetail(meetingId: string) {
  return request<Envelope<AdminMeetingDetailData>>(`/admin/meetings/${meetingId}`, {
    method: 'GET',
  });
}

export async function updateAdminMeetingStatus(
  meetingId: string,
  targetStatus: string,
  note?: string,
) {
  return request<Envelope<{ success: boolean }>>(`/admin/meetings/${meetingId}/status`, {
    method: 'POST',
    data: {
      target_status: targetStatus,
      note,
    },
  });
}

export async function assignAdminMeetingLeader(
  meetingId: string,
  leaderUserId: string,
  note?: string,
) {
  return request<Envelope<{ success: boolean }>>(`/admin/meetings/${meetingId}/assign-leader`, {
    method: 'POST',
    data: {
      leader_user_id: leaderUserId,
      note,
    },
  });
}

export async function getAdminCustomIntents(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminCustomIntentListItem>>>('/admin/custom-intents', {
    method: 'GET',
    params,
  });
}

export async function getAdminCustomIntentDetail(intentId: string) {
  return request<Envelope<AdminCustomIntentDetailData>>(`/admin/custom-intents/${intentId}`, {
    method: 'GET',
  });
}

export async function updateAdminCustomIntent(
  intentId: string,
  data: { target_status: string; contact_note?: string },
) {
  return request<Envelope<{ success: boolean }>>(`/admin/custom-intents/${intentId}`, {
    method: 'POST',
    data,
  });
}

export async function getAdminOrders(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminOrderListItem>>>('/admin/orders', {
    method: 'GET',
    params,
  });
}

export async function getAdminEarnings(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminEarningListItem>>>('/admin/earnings', {
    method: 'GET',
    params,
  });
}

export async function getAdminIncomeHierarchy(params?: Record<string, unknown>) {
  return request<Envelope<AdminIncomeHierarchyData>>('/admin/income-hierarchy', {
    method: 'GET',
    params,
  });
}

export async function getAdminArticles(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminArticleListItem>>>('/admin/articles', {
    method: 'GET',
    params,
  });
}

export async function createAdminArticle(data: Record<string, unknown>) {
  return request<Envelope<{ article_id: string }>>('/admin/articles', {
    method: 'POST',
    data,
  });
}

export async function updateAdminArticle(articleId: string, data: Record<string, unknown>) {
  return request<Envelope<{ success: boolean }>>(`/admin/articles/${articleId}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteAdminArticle(articleId: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/articles/${articleId}`, {
    method: 'DELETE',
  });
}

export async function getAdminPartnerServices(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminPartnerServiceListItem>>>('/admin/partner-services', {
    method: 'GET',
    params,
  });
}

export async function upsertAdminPartnerService(data: Record<string, unknown>) {
  return request<Envelope<{ success?: boolean; partner_service_id?: string }>>(
    '/admin/partner-services',
    {
      method: 'POST',
      data,
    },
  );
}

export async function deleteAdminPartnerService(partnerServiceId: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/partner-services/${partnerServiceId}`, {
    method: 'DELETE',
  });
}

export async function getAdminCompanyStores(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminCompanyStoreListItem>>>('/admin/company-stores', {
    method: 'GET',
    params,
  });
}

export async function upsertAdminCompanyStore(data: Record<string, unknown>) {
  return request<Envelope<{ success?: boolean; company_store_id?: string }>>(
    '/admin/company-stores',
    {
      method: 'POST',
      data,
    },
  );
}

export async function deleteAdminCompanyStore(companyStoreId: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/company-stores/${companyStoreId}`, {
    method: 'DELETE',
  });
}

export async function getAdminTopics(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminTopicListItem>>>('/admin/topics', {
    method: 'GET',
    params,
  });
}

export async function createAdminTopic(data: {
  title: string;
  intro?: string;
  icon_url: string;
  sort_order?: number;
  status?: 'active' | 'inactive';
}) {
  return request<Envelope<AdminTopicListItem>>('/admin/topics', {
    method: 'POST',
    data,
  });
}

export async function updateAdminTopic(topicId: string, data: {
  title?: string;
  intro?: string;
  icon_url?: string;
  sort_order?: number;
  status?: 'active' | 'inactive';
}) {
  return request<Envelope<{ success: boolean }>>(`/admin/topics/${topicId}`, {
    method: 'PUT',
    data,
  });
}

export async function updateAdminTopicStatus(topicId: string, status: 'active' | 'inactive') {
  return request<Envelope<{ success: boolean }>>(`/admin/topics/${topicId}/status`, {
    method: 'POST',
    data: { status },
  });
}

export async function deleteAdminTopic(topicId: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/topics/${topicId}`, {
    method: 'DELETE',
  });
}

export async function getAdminWithdrawals(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminWithdrawalListItem>>>('/admin/withdrawals', {
    method: 'GET',
    params,
  });
}

export async function getAdminWithdrawalDetail(withdrawalId: string) {
  return request<Envelope<AdminWithdrawalDetailData>>(`/admin/withdrawals/${withdrawalId}`, {
    method: 'GET',
  });
}

export async function approveWithdrawal(withdrawalId: string, reviewNote: string) {
  return request<Envelope<{ success: boolean }>>(
    `/admin/withdrawals/${withdrawalId}/approve`,
    {
      method: 'POST',
      data: { review_note: reviewNote },
    },
  );
}

export async function rejectWithdrawal(withdrawalId: string, reviewNote: string) {
  return request<Envelope<{ success: boolean }>>(
    `/admin/withdrawals/${withdrawalId}/reject`,
    {
      method: 'POST',
      data: { review_note: reviewNote },
    },
  );
}

export async function markWithdrawalPaid(withdrawalId: string, reviewNote: string) {
  return request<Envelope<{ success: boolean }>>(
    `/admin/withdrawals/${withdrawalId}/pay`,
    {
      method: 'POST',
      data: { review_note: reviewNote },
    },
  );
}

export async function getAdminPosts(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminPostListItem>>>('/admin/posts', {
    method: 'GET',
    params,
  });
}

export async function auditAdminPost(postId: string, targetStatus: string, reason: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/posts/${postId}/audit`, {
    method: 'POST',
    data: { target_status: targetStatus, reason },
  });
}

export async function deleteAdminPost(postId: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/posts/${postId}/delete`, {
    method: 'POST',
  });
}

export async function getAdminMemberAlbums(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminMemberAlbumListItem>>>('/admin/member-albums', {
    method: 'GET',
    params,
  });
}

export async function auditAdminMemberAlbum(photoId: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/member-albums/${photoId}/audit`, {
    method: 'POST',
    data: { target_status: 'passed' },
  });
}

export async function deleteAdminMemberAlbum(photoId: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/member-albums/${photoId}/delete`, {
    method: 'POST',
  });
}

export async function getAdminStores(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminStoreListItem>>>('/admin/stores', {
    method: 'GET',
    params,
  });
}

export async function getAdminStoreApplications(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminStoreApplicationListItem>>>('/admin/store-applications', {
    method: 'GET',
    params,
  });
}

export async function getDistrictList() {
  return request<Envelope<DistrictListData>>('/districts', {
    method: 'GET',
  });
}

export async function getAdminStoreDetail(storeId: string) {
  return request<Envelope<AdminStoreDetailData>>(`/admin/stores/${storeId}`, {
    method: 'GET',
  });
}

export async function createAdminStore(data: Record<string, unknown>) {
  return request<Envelope<{ store_id: string }>>('/admin/stores', {
    method: 'POST',
    data,
  });
}

export async function auditAdminStoreApplication(
  applicationId: string,
  targetStatus: string,
  note?: string,
) {
  return request<Envelope<{ success: boolean }>>(`/admin/store-applications/${applicationId}/audit`, {
    method: 'POST',
    data: {
      target_status: targetStatus,
      note,
    },
  });
}

export async function updateAdminStore(storeId: string, data: Record<string, unknown>) {
  return request<Envelope<{ success: boolean }>>(`/admin/stores/${storeId}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteAdminStore(storeId: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/stores/${storeId}`, {
    method: 'DELETE',
  });
}

export async function getAdminStoreLeaders(storeId: string) {
  return request<
    Envelope<{
      list: {
        leader_user_id: string;
        leader_name?: string | null;
        service_city?: string | null;
        assignment_status?: string | null;
        created_at?: string | null;
      }[];
    }>
  >(`/admin/stores/${storeId}/leaders`, {
    method: 'GET',
  });
}

export async function assignAdminStoreLeader(storeId: string, leaderUserId: string) {
  return request<Envelope<{ success: boolean }>>(`/admin/stores/${storeId}/leaders`, {
    method: 'POST',
    data: {
      leader_user_id: leaderUserId,
    },
  });
}

export async function removeAdminStoreLeader(storeId: string, leaderUserId: string) {
  return request<Envelope<{ success: boolean }>>(
    `/admin/stores/${storeId}/leaders/${leaderUserId}/remove`,
    {
      method: 'POST',
    },
  );
}

export async function getAdminSettings() {
  return request<Envelope<AdminSettingsData>>('/admin/settings', { method: 'GET' });
}

export async function updateAdminSettings(data: Record<string, unknown>) {
  return request<Envelope<{ success: boolean }>>('/admin/settings', {
    method: 'PUT',
    data,
  });
}

export async function patchAdminSettingsSection(section: string, data: Record<string, unknown>) {
  return request<Envelope<{ success: boolean }>>(`/admin/settings/${section}`, {
    method: 'PATCH',
    data,
  });
}

export async function uploadAdminImage(file: File) {
  const token = localStorage.getItem('admin_token');
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch('/api/v1/admin/uploads/images', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: formData,
  });

  const payload = (await response.json()) as Envelope<AdminUploadImageData>;

  if (!response.ok || payload.code !== 0) {
    throw new Error(payload.message || 'upload failed');
  }

  return payload.data;
}

export async function getAdminAccounts(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminAccountItem>>>('/admin/admins', {
    method: 'GET',
    params,
  });
}

export async function createAdminAccount(data: {
  account: string;
  password: string;
  nickname?: string;
  admin_role_id?: string | null;
  notes?: string;
}) {
  return request<Envelope<AdminAccountItem>>('/admin/admins', {
    method: 'POST',
    data,
  });
}

export async function updateAdminAccount(
  adminUserId: string,
  data: {
    account?: string;
    password?: string;
    nickname?: string;
    admin_role_id?: string | null;
    account_status?: string;
    notes?: string;
    reason?: string;
  },
) {
  return request<Envelope<{ success: boolean; session_version: number }>>(
    `/admin/admins/${adminUserId}`,
    {
      method: 'PUT',
      data,
    },
  );
}

export async function forceLogoutAdminAccount(adminUserId: string, reason: string) {
  return request<Envelope<{ success: boolean; session_version: number }>>(
    `/admin/admins/${adminUserId}/force-logout`,
    {
      method: 'POST',
      data: { reason },
    },
  );
}

export async function getPermissionCatalog() {
  return request<Envelope<{ groups: PermissionCatalogGroup[] }>>(
    '/admin/permission-catalog',
    { method: 'GET' },
  );
}

export async function getAdminRoles() {
  return request<Envelope<{ list: AdminRoleItem[] }>>('/admin/roles', {
    method: 'GET',
  });
}

export async function createAdminRole(data: {
  code: string;
  name: string;
  description?: string;
  permissions: string[];
}) {
  return request<Envelope<AdminRoleItem>>('/admin/roles', {
    method: 'POST',
    data,
  });
}

export async function updateAdminRole(
  roleId: string,
  data: {
    name?: string;
    description?: string;
    permissions?: string[];
    reason?: string;
  },
) {
  return request<Envelope<AdminRoleItem>>(`/admin/roles/${roleId}`, {
    method: 'PUT',
    data,
  });
}

export async function deleteAdminRole(roleId: string, reason?: string) {
  return request<Envelope<{ success: boolean; affected_account_count: number }>>(
    `/admin/roles/${roleId}`,
    {
      method: 'DELETE',
      data: { reason },
    },
  );
}

export async function getAdminLogs(params?: Record<string, unknown>) {
  return request<Envelope<PaginatedList<AdminLogItem>>>('/admin/logs', {
    method: 'GET',
    params,
  });
}

export async function getAdminReports() {
  return request<Envelope<AdminReportsData>>('/admin/reports', {
    method: 'GET',
  });
}
